import { isAdmin } from 'shared/domain/role/isAdmin';
import { isDefined } from 'shared/domain/role/isDefined';
import { isManagerOrAdmin } from 'shared/domain/role/isManagerOrAdmin';
import { isStandard } from 'shared/domain/role/isStandard';
import { UserWithPermissionsInDto } from 'shared/domain/user/types/inDto';
import { isBelowSuperAdminPermission, UserRole } from 'shared/types/userRole';

/**
 * Returns the user's organization-level admin role if they have one.
 *
 * At the client/organization level, only two states exist:
 * - OrganizationAdminPermission: has `role: organizationAdmin`
 * - BelowOrganizationAdminPermission: has NO `role` property (only `projects[]`)
 *
 * Project-level roles (standard, manager, projectAdmin) are stored in
 * `client.projects[].role`, not as `client.role`.
 *
 * This means early return is safe - we're checking if user is OrgAdmin
 * for ANY organization, not comparing role priorities.
 *
 * NOTE: Unlike getRoleFromUserWithPermissionsInDto (which returns the role
 * for a SPECIFIC project/organization), this function intentionally returns
 * any OrgAdmin role regardless of organization context. This is correct for
 * global access checks like "does this user have admin access anywhere?"
 */
function getBaseRole(
  user: UserWithPermissionsInDto
): UserRole | undefined {
  const basePermissions = user.basePermissions;

  if ('role' in basePermissions && basePermissions.role) {
    return basePermissions.role;
  }

  if (isBelowSuperAdminPermission(basePermissions)) {
    for (const client of basePermissions.clients) {
      if ('role' in client && client.role) {
        return client.role;
      }
    }
  }

  return undefined;
}

export function hasUserPermissionsData(
  user: UserWithPermissionsInDto
): boolean {
  if (!user._id) {
    return false;
  }

  return getBaseRole(user) !== undefined || user.permissions.length > 0;
}

export function userHasGlobalRoleAccess(
  user: UserWithPermissionsInDto,
  matchesRole: (role: UserRole) => boolean
): boolean {
  const baseRole = getBaseRole(user);
  if (baseRole && matchesRole(baseRole)) {
    return true;
  }

  return user.permissions.some(
    (permission) => permission.role && matchesRole(permission.role)
  );
}

export function userHasAboveStandardAccess(
  user: UserWithPermissionsInDto
): boolean {
  return userHasGlobalRoleAccess(
    user,
    (role) => isDefined(role) && !isStandard(role)
  );
}

export function userHasManagerOrAdminAccess(
  user: UserWithPermissionsInDto
): boolean {
  return userHasGlobalRoleAccess(user, isManagerOrAdmin);
}

export function userHasAdminAccess(user: UserWithPermissionsInDto): boolean {
  return userHasGlobalRoleAccess(user, isAdmin);
}
